import {
  type ChangeEvent,
  type FocusEvent,
  forwardRef,
  memo,
  type MutableRefObject,
  useCallback,
  useEffect,
} from "react";

import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import classes from "./SearchInput.module.less";
import { PopperTooltip } from "~/common/components/Tooltip";
import { LinkButton, LinkType } from "~/common/components/Link";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { Button, styled } from "~/common/mui";
import { C_ON_PRIMARY_CONTAINER, C_PRIMARY_CONTAINER } from "~/common/components/icon/Colors";

interface SearchInputProps {
  value: string;
  autofocus?: boolean;
  onChange?: (text: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onClear?: () => void;
  placeholder?: string;
  variant?: "header" | "cad";
}

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value: searchText, autofocus, onChange, onBlur, onFocus, onClear, placeholder, variant }, ref) => {
    const { t } = useTranslation();
    const changeCb = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const text = e.currentTarget.value.trimLeft();
        if (onChange) {
          onChange(text);
        }
      },
      [onChange],
    );

    const focusController = (e: FocusEvent<HTMLInputElement>) => {
      const target = e.currentTarget as HTMLInputElement;
      if (autofocus && target) {
        target.select();
      }
      onFocus?.();
    };

    const isDesktop = useIsMinDesktopSize();

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        const inputRef = ref as MutableRefObject<HTMLInputElement>;
        if (event.key === "Escape") {
          event.preventDefault();
          inputRef.current.blur();
        }
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, []);

    const pageSize = useNumericScreenSize();

    return (
      <div
        className={classNames(
          classes.root,
          variant === "cad" && classes.variantCAD,
          variant === "header" && classes.variantHeader,
        )}
      >
        <input
          ref={ref}
          value={searchText}
          maxLength={100}
          id={"searchBoxInput"}
          name="text"
          placeholder={placeholder}
          onChange={changeCb}
          onBlur={onBlur}
          autoFocus={autofocus}
          onFocus={focusController}
          className={classes.input}
          autoComplete="off"
          role="searchbox"
          // TODO - experiment with type = search
        />
        {variant !== "cad" && searchText && (
          <LinkButton
            className={classNames(classes.clearButton, !searchText && classes.hide)}
            aria-label={t("searchPage.refinement.button.clear")}
            onClick={onClear}
            variant={LinkType.SECONDARY}
            type={"button"}
          >
            <Icon className={"scha__link__icon"} type={IconType.Close} />
          </LinkButton>
        )}
        <PopperTooltip hideTooltip={!isDesktop || variant === "cad"} tooltipContent={t("orderListPage.searchBtn")}>
          <SearchButton
            disabled={!searchText}
            aria-label={t("generic.button.search")}
            type={"submit"}
            id={"searchBoxSubmit"}
            className={classes.searchBtn}
          >
            <Icon type={IconType.Search} size={pageSize >= useNumericScreenSize.desktopS ? 20 : 16} />
          </SearchButton>
        </PopperTooltip>
      </div>
    );
  },
);

export default memo(SearchInput);

const SearchButton = styled(Button)(({ theme }) => ({
  minHeight: "unset",
  borderRadius: 0,
  background: C_PRIMARY_CONTAINER,
  color: C_ON_PRIMARY_CONTAINER,
  minWidth: "unset",
  [theme.breakpoints.up("desktop-s")]: {
    paddingInline: theme.spacing(3),
  },
}));
